// src/utils/activityLogger.ts
import { activityLogAPI } from '../api/logs';

class ActivityLogger {
    private async log(action: string, entityType?: string, entityId?: string | number, metadata?: any) {
        try {
            await activityLogAPI.logActivity({
                action,
                entity_type: entityType,
                entity_id: entityId?.toString(),
                metadata
            });
        } catch (error) {
            // Silently fail - logging shouldn't break the main flow
            console.debug('Failed to log activity:', error);
        }
    }

    // Product actions
    productCreated(productId: string | number, productData: any) {
        this.log('PRODUCT_CREATED', 'product', productId, {
            sku: productData.sku,
            title: productData.title,
            hasVariants: productData.has_variants,
            variantCount: productData.variants?.length
        });
    }

    productUpdated(productId: string | number, changes: any) {
        this.log('PRODUCT_UPDATED', 'product', productId, {
            changedFields: Object.keys(changes),
            ...changes
        });
    }

    productDeleted(productId: string | number, productTitle: string) {
        this.log('PRODUCT_DELETED', 'product', productId, {
            title: productTitle
        });
    }

    // Variant actions
    variantAdded(productId: string | number, variantData: any) {
        this.log('VARIANT_ADDED', 'product_variant', variantData.id, {
            productId,
            variantName: variantData.name,
            skuSuffix: variantData.sku_suffix
        });
    }

    variantUpdated(productId: string | number, variantId: string | number, changes: any) {
        this.log('VARIANT_UPDATED', 'product_variant', variantId, {
            productId,
            ...changes
        });
    }

    variantDeleted(productId: string | number, variantData: any, imageHandling: 'moved' | 'deleted') {
        this.log('VARIANT_DELETED', 'product_variant', variantData.id, {
            productId,
            variantName: variantData.name,
            hadImages: variantData.imagesCount > 0,
            imageHandling,
            remainingVariants: variantData.remainingCount
        });
    }

    // Image actions
    imagesAdded(productId: string | number, count: number, variantId?: string | number) {
        this.log('IMAGES_ADDED', 'product_image', productId, {
            count,
            variantId: variantId || 'default'
        });
    }

    imagesReorganized(productId: string | number, changes: any) {
        this.log('IMAGES_REORGANIZED', 'product', productId, changes);
    }

    variantsDeleted(productId: string | number, variants: any[]) {
        this.log('VARIANTS_DELETED', 'product', productId, {
            count: variants.length,
            variants: variants.map(v => ({ id: v.id, name: v.name }))
        });
    }

    imagesDeleted(productId: string | number, count: number) {
        this.log('IMAGES_DELETED', 'product', productId, { count });
    }

    keywordsUpdated(productId: string | number, changes: any) {
        this.log('KEYWORDS_UPDATED', 'product', productId, changes);
    }

    categoriesUpdated(productId: string | number, changes: any) {
        this.log('CATEGORIES_UPDATED', 'product', productId, changes);
    }
}

export const activityLogger = new ActivityLogger();